import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Clock, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Plus, Timer, Ban,
} from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import { ShortLeaveRequestView, ShortLeaveUsageView } from '../../../models/attendance';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getFullUrl } from '../../../utils/url';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate } from '../../../utils/appDateFormat';
import { DateField, TimeField } from '../../../components/dateFields';
import { todayLocalDateParam } from '@/utils/apiDate';

interface ShortLeaveProps {
  userId: string;
  company: { id: number };
  role: string;
}

type TabId = 'pending' | 'my';

const statusConfig: Record<string, { bgClass: string; dotClass: string; icon: React.ElementType }> = {
  Pending: { bgClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500', icon: Clock },
  Approved: { bgClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500', icon: CheckCircle2 },
  Rejected: { bgClass: 'bg-red-100 text-red-800', dotClass: 'bg-red-500', icon: XCircle },
  Cancelled: { bgClass: 'bg-muted text-muted-foreground', dotClass: 'bg-gray-400', icon: Ban },
};

function formatDate(dateStr: string): string {
  return appDate(new Date(dateStr));
}

const ShortLeave: React.FC<ShortLeaveProps> = ({ role }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const isManagerOrHr = ['Manager', 'Hr', 'Chief'].includes(role);

  const [activeTab, setActiveTab] = useState<TabId>(isManagerOrHr ? 'pending' : 'my');
  const [pendingApprovals, setPendingApprovals] = useState<ShortLeaveRequestView[]>([]);
  const [myRequests, setMyRequests] = useState<ShortLeaveRequestView[]>([]);
  const [usage, setUsage] = useState<ShortLeaveUsageView | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [requestModal, setRequestModal] = useState(false);
  const [approvalModal, setApprovalModal] = useState<{ request: ShortLeaveRequestView; action: 'approve' | 'reject' } | null>(null);
  const [cancelModal, setCancelModal] = useState<ShortLeaveRequestView | null>(null);
  const [requestForm, setRequestForm] = useState({
    date: todayLocalDateParam(),
    startTime: '09:00',
    endTime: '10:00',
    reason: '',
  });
  const [approvalComments, setApprovalComments] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, myRes, usageRes] = await Promise.all([
        isManagerOrHr ? LeavesApi.getPendingShortLeaveApprovals() : Promise.resolve({ errors: [], payload: [] as ShortLeaveRequestView[] }),
        LeavesApi.getMyShortLeaves(),
        LeavesApi.getShortLeaveMonthlyUsage(),
      ]);
      if (pendingRes.errors.length === 0) setPendingApprovals(pendingRes.payload);
      if (myRes.errors.length === 0) setMyRequests(myRes.payload);
      if (usageRes.errors.length === 0) setUsage(usageRes.payload);
    } catch {
      setToast({ message: t('shortLeaveLoadError'), type: 'error' });
    }
    setLoading(false);
  }, [isManagerOrHr, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleRequest = async () => {
    setActionLoading(-1);
    try {
      const result = await LeavesApi.requestShortLeave(requestForm);
      if (result.errors.length === 0) {
        setToast({ message: t('shortLeaveRequested'), type: 'success' });
        setRequestModal(false);
        setRequestForm({ date: todayLocalDateParam(), startTime: '09:00', endTime: '10:00', reason: '' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('shortLeaveRequestError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('shortLeaveRequestError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleApproval = async () => {
    if (!approvalModal) return;
    setActionLoading(approvalModal.request.id);
    try {
      const fn = approvalModal.action === 'approve' ? LeavesApi.approveShortLeave : LeavesApi.rejectShortLeave;
      const result = await fn(approvalModal.request.id, { comments: approvalComments || undefined });
      if (result.errors.length === 0) {
        setToast({ message: t(approvalModal.action === 'approve' ? 'shortLeaveApproved' : 'shortLeaveRejected'), type: 'success' });
        setApprovalModal(null);
        setApprovalComments('');
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('shortLeaveActionError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('shortLeaveActionError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setActionLoading(cancelModal.id);
    try {
      const result = await LeavesApi.cancelShortLeave(cancelModal.id);
      if (result.errors.length === 0) {
        setToast({ message: t('shortLeaveCancelled'), type: 'success' });
        setCancelModal(null);
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('shortLeaveCancelError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('shortLeaveCancelError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const tabs = [
    ...(isManagerOrHr ? [{ id: 'pending' as TabId, label: t('Pending Approvals'), count: pendingApprovals.length }] : []),
    { id: 'my' as TabId, label: t('My Short Leaves'), count: myRequests.length },
  ];

  const renderStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.Pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgClass}`}>
        <Icon className="w-3 h-3" />
        {t(status)}
      </span>
    );
  };

  const renderUsageCard = () => {
    if (!usage || usage.monthlyLimit === 0) return null;
    const usedPercent = usage.monthlyLimit > 0 ? Math.min(100, (usage.hoursUsedThisMonth / usage.monthlyLimit) * 100) : 0;
    return (
      <div className="bg-background rounded-xl border border-border p-4 mb-6" data-testid="short-leave-usage">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">{t('Monthly Usage')}</h3>
          <span className="text-xs text-muted-foreground">
            {usage.hoursUsedThisMonth.toFixed(1)} / {usage.monthlyLimit.toFixed(1)} {t('hours')}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all ${usedPercent >= 90 ? 'bg-red-500' : usedPercent >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
            style={{ width: `${usedPercent}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-xs text-muted-foreground">{t('Used')}: {usage.hoursUsedThisMonth.toFixed(1)}h</span>
          <span className="text-xs text-muted-foreground">{t('Remaining')}: {usage.hoursRemaining.toFixed(1)}h</span>
        </div>
      </div>
    );
  };

  const renderRequestCard = (req: ShortLeaveRequestView, showActions: boolean) => (
    <div key={req.id} className="bg-background rounded-xl border border-border p-5 hover:shadow-md transition-shadow" data-testid={`short-leave-card-${req.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {getFullUrl(req.userImageUrl) ? (
              <img src={getFullUrl(req.userImageUrl)!} alt={req.userName} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <Timer className="w-5 h-5 text-primary" />
            )}
          </div>
          <div>
            <p className="font-semibold text-foreground">{req.userName}</p>
            <p className="text-xs text-muted-foreground">{formatDate(req.date)}</p>
          </div>
        </div>
        {renderStatusBadge(req.status)}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">{t('Start Time')}</p>
          <p className="text-sm font-medium text-foreground">{req.startTime}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('End Time')}</p>
          <p className="text-sm font-medium text-foreground">{req.endTime}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('Hours')}</p>
          <p className="text-sm font-medium text-foreground">{req.hoursRequested.toFixed(1)}h</p>
        </div>
      </div>

      {req.reason && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">{t('Reason')}</p>
          <p className="text-sm text-foreground mt-0.5">{req.reason}</p>
        </div>
      )}

      {req.managerComments && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">{t('Manager Comments')}</p>
          <p className="text-sm text-foreground mt-0.5">{req.managerComments}</p>
        </div>
      )}

      {showActions && req.status === 'Pending' && (
        <div className="mt-4 flex gap-2">
          {activeTab === 'pending' && isManagerOrHr ? (
            <>
              <Button
                onClick={() => { setApprovalModal({ request: req, action: 'approve' }); setApprovalComments(''); }}
                disabled={actionLoading === req.id}
                variant="success"
                className="flex-1"
                data-testid={`approve-short-leave-${req.id}`}
                iconName="CheckCircle2"
              >
                {t('Approve')}
              </Button>
              <Button
                onClick={() => { setApprovalModal({ request: req, action: 'reject' }); setApprovalComments(''); }}
                disabled={actionLoading === req.id}
                variant="destructive"
                className="flex-1"
                data-testid={`reject-short-leave-${req.id}`}
                iconName="XCircle"
              >
                {t('Reject')}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setCancelModal(req)}
              disabled={actionLoading === req.id}
              variant="secondary"
              data-testid={`cancel-short-leave-${req.id}`}
              iconName="Ban"
            >
              {t('Cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const currentList = activeTab === 'pending' ? pendingApprovals : myRequests;

  return (
    <PageShell
      title={t('Short Leave')}
      description={t('shortLeaveDescription')}
      icon={Timer}
      accentColor="emerald"
      maxWidth="5xl"
      stats={[
        { label: t('Pending Approvals'), value: pendingApprovals.length, color: 'text-amber-600' },
        { label: t('My Short Leaves'), value: myRequests.length },
        ...(usage ? [{ label: t('Monthly Usage'), value: `${usage.hoursUsedThisMonth.toFixed(1)}h / ${usage.monthlyLimit.toFixed(1)}h` }] : []),
      ]}
      headerActions={
        <Button
          variant="outline"
          onClick={() => setRequestModal(true)}
          data-testid="request-short-leave-btn"
          iconName="Plus"
        >
          {t('Request Short Leave')}
        </Button>
      }
      className="min-h-screen bg-background"
    >
      {/* Usage */}
      <div className="pt-6">
        {renderUsageCard()}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-xl mb-6" data-testid="short-leave-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : currentList.length === 0 ? (
        <div className="text-center py-20" data-testid="short-leave-empty">
          <Timer className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">{t('noShortLeaves')}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {activeTab === 'pending' ? t('noPendingShortLeaveApprovals') : t('noShortLeaveRequests')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {currentList.map(req => renderRequestCard(req, true))}
        </div>
      )}

      {/* Request Modal */}
      <Dialog open={requestModal} onOpenChange={setRequestModal}>
        <DialogContent data-testid="request-short-leave-modal">
          <DialogHeader>
            <DialogTitle>{t('Request Short Leave')}</DialogTitle>
            <DialogDescription>{t('shortLeaveRequestDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Date')}</label>
              <DateField
                value={requestForm.date}
                onChange={(v) => setRequestForm({ ...requestForm, date: v })}
                data-testid="short-leave-date"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('Start Time')}</label>
                <TimeField
                  value={requestForm.startTime}
                  onChange={(v) => setRequestForm({ ...requestForm, startTime: v })}
                  data-testid="short-leave-start-time"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('End Time')}</label>
                <TimeField
                  value={requestForm.endTime}
                  onChange={(v) => setRequestForm({ ...requestForm, endTime: v })}
                  data-testid="short-leave-end-time"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Reason')}</label>
              <textarea
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                placeholder={t('shortLeaveReasonPlaceholder')}
                data-testid="short-leave-reason"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setRequestModal(false)} variant="outline" data-testid="cancel-request-btn">
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleRequest}
              disabled={actionLoading === -1 || !requestForm.date || !requestForm.startTime || !requestForm.endTime}
              loading={actionLoading === -1}
              data-testid="submit-request-btn"
            >
              {t('Submit Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={!!approvalModal} onOpenChange={(o) => { if (!o) setApprovalModal(null); }}>
        <DialogContent data-testid="approval-modal">
          <DialogHeader>
            <DialogTitle>
              {approvalModal?.action === 'approve' ? t('Approve Short Leave') : t('Reject Short Leave')}
            </DialogTitle>
            <DialogDescription>
              {approvalModal?.request.userName} - {approvalModal ? formatDate(approvalModal.request.date) : ''} ({approvalModal?.request.startTime} - {approvalModal?.request.endTime})
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <label className="block text-sm font-medium text-foreground mb-1">{t('Comments')}</label>
            <textarea
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
              placeholder={t('shortLeaveCommentsPlaceholder')}
              data-testid="approval-comments"
            />
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setApprovalModal(null)} variant="outline" data-testid="leave-short-approval-cancel">
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleApproval}
              disabled={actionLoading === approvalModal?.request.id}
              variant={approvalModal?.action === 'approve' ? 'success' : 'destructive'}
              loading={actionLoading === approvalModal?.request.id}
              data-testid="confirm-approval-btn"
            >
              {approvalModal?.action === 'approve' ? t('Approve') : t('Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={!!cancelModal} onOpenChange={(o) => { if (!o) setCancelModal(null); }}>
        <DialogContent size="sm" data-testid="cancel-modal">
          <DialogBody className="text-center pt-6">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground">{t('Cancel Short Leave')}</h3>
            <p className="text-sm text-muted-foreground mt-2">
              {cancelModal && t('confirmCancelShortLeave', { date: formatDate(cancelModal.date), time: `${cancelModal.startTime} - ${cancelModal.endTime}` })}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setCancelModal(null)} variant="outline" className="flex-1" data-testid="leave-short-cancel-dismiss">
              {t('Keep')}
            </Button>
            <Button
              onClick={handleCancel}
              disabled={cancelModal ? actionLoading === cancelModal.id : false}
              variant="destructive"
              className="flex-1"
              loading={cancelModal ? actionLoading === cancelModal.id : false}
              data-testid="confirm-cancel-btn"
            >
              {t('Cancel Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 end-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
    </PageShell>
  );
};

const mapStateToProps = (state: { user: { userId: string; company: { id: number }; role: string } }) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(ShortLeave);
